import isEmpty from 'lodash/isEmpty';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Avatar from '../../../../component/avatar';
import AiLoader from '../../../../assets/images/AI_Loader_Gradient_128x128_v2.png';
import SVGIcon from '../../../../assets/images/svg/SVGIcon';
import AssignBoxV2 from '../../../../component/dropdowns/assignPopup';
import { useTaskAssignActions } from '../../../../component/dropdowns/assignPopup/assignActions';
import { RenderFilePreview } from '../../../../component/renderFilePreview';

import {
  AVATAR_BORDER,
  FILE_TYPES,
  ICON_BUTTON_SIZE,
  ICON_BUTTON_TYPE,
  LABEL_COLOUR_HASH,
  LINK_FILE_DATA,
  MENU_OPTIONS,
  COMMENT_TYPE,
  ICON_SIZE
} from '../../../../global/constants';
import {
  calculateDueTime,
  extensionName,
  getFloatTimeValue,
  convertBase64ToFile,
  getFirebaseUrlFromFile,
  handleFileUpload,
  removeFileFromFirebase,
  buildTaskAttachmentFolder,
  buildTaskDescriptionImageFolder,
  getRawComment
} from '../../../../helpers/common';
import { StatusListInterface } from '../../../../interfaces/ProjectInterface';
import Linkify from 'react-linkify';
import { nanoid } from 'nanoid';
import {
  HeaderContent,
  SubTaskList,
  Form,
  TextBlock,
  SubtaskTitle,
  FormBlock,
  MainTask,
  Form2,
  TaskLabel,
  TaskArea,
  TaskAreaFile,
  SubTaskSection,
  AddSubTaskSection,
  AddSubTask,
  SubtaskHeadLeft,
  TaskIndicator,
  TaskIndicatorText,
  SubtaskHeadRight,
  DivSubTask,
  Subtask,
  TaskItem,
  IconHidden,
  Form1,
  Label,
  AssignGroup,
  AvatarImage,
  Right,
  MultipleLabel,
  DropBlock,
  DotIcon,
  DropLabel,
  DueDate,
  DetailTask,
  BottomContent,
  Dot,
  GithubMainDiv,
  GithubTitle,
  GithubSmallIcon,
  LabelIcon,
  MergeText,
  MergedLabel,
  Texts,
  GithubPRSection,
  ButtonIcon,
  LinkSection,
  LinkCard,
  Content,
  SocialIcon,
  LinkText,
  LinkRightContent,
  TimeText,
  LinkTitle,
  TooltipContent,
  LabelContainer,
  ContentRightSection,
  DropDiv,
  SubTaskDiv,
  IconDiv,
  TaskTitleDiv,
  AttachmentContainer,
  HiddenFileInput
} from './styles';
import {
  CommentFilesInterface,
  SubTaskPayload,
  GeneratedSubTask,
  TaskFileData
} from '../../../../interfaces/TaskInterface';
import {
  updateTaskDetails,
  deleteTaskLink,
  generateSubTasks,
  createBulkTask,
  dispatchTaskSuccess,
  dispatchTaskError,
  createNewComment
} from '../../../../services/taskServices';
import { captureException } from '../../../../services/logService';
import { useMobileDevice } from '../../../../global/useMobile';
import { useHistory } from 'react-router-dom';
import { Dropdown, DropdownItem } from '../../../../component/dropdown';
import Modal from '../../../../component/modals/modal';
import DeleteModal from '../../../../component/modals/deleteModal';
import LinkFileModal from '../../../../component/modals/linkFileModal';
import copy from 'clipboard-copy';
import Tooltip from '../../../../component/tooltip';
import { rootStore } from '../../../../mobx/rootStore';
import { MainTooltipDiv, StatusDropdown, TaskCount, TooltipDiv } from '../styles';
import { TaskModel } from '../../../../mobx/models/task';
import { UserModel } from '../../../../mobx/models/user';
import MyCustomContextMenuV2 from '../../../../component/customContextMenu';
import TaskStatusDropdownV2 from '../../../../component/dropdowns/taskStatusDropdown';
import PriorityDropdownV2 from '../../../../component/dropdowns/priorityDropdown';
import { observer } from 'mobx-react-lite';
import IconButton from '../../../../component/button/iconButton';
import TaskTitle from './taskTitle';
import { RawFile } from '../../../../mobx/interfaces/task';
import FileUploadCard from '../../../../component/fileUploadCard';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import TiptapEditor from '../../../../component/tiptapEditor';
import { TiptapEditorRef } from '../../../../interfaces/tiptapInterface';
import CheckboxInput from '../../../../component/input/checkboxInput';
import Image from '../../../../component/image';
import { ROUTES } from '../../../../routes/urls';

dayjs.extend(relativeTime);

interface SortableSubTaskItemProps {
  id: string;
  children: (props: {
    isDragging: boolean;
    dragHandleAttributes: ReturnType<typeof useSortable>['attributes'];
    dragHandleListeners: ReturnType<typeof useSortable>['listeners'];
  }) => React.ReactNode;
}

// observer() so the row's render-prop reads (item.users, item.labels, ...) are tracked.
// Under the in-place merge upsert the parent list no longer re-renders on per-task
// field changes, so each row must observe the fields it renders to repaint itself.
const SortableSubTaskItem: React.FC<SortableSubTaskItemProps> = observer(({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style: React.CSSProperties = {
    transform: CSS.Translate.toString(transform),
    transition: transition || undefined,
    opacity: isDragging ? 0.4 : 1
  };

  return (
    <div ref={setNodeRef} style={style}>
      {children({ isDragging, dragHandleAttributes: attributes, dragHandleListeners: listeners })}
    </div>
  );
});

interface SubTaskAssignBoxProps {
  task: TaskModel;
  projectUsers: UserModel[];
  isMultiAssignee: boolean;
}

const SubTaskAssignBox: React.FC<SubTaskAssignBoxProps> = observer(({ task, projectUsers, isMultiAssignee }) => {
  const assignActions = useTaskAssignActions(task);
  return (
    <AssignBoxV2
      projectUsers={projectUsers}
      context={{ mode: 'task', currentTask: task }}
      onAssign={assignActions.onAssign}
      onUnassign={assignActions.onUnassign}
      isMultiAssignee={isMultiAssignee}
    />
  );
});

interface Props {
  onClickTaskCheckbox: (task: TaskModel, status: boolean) => void;
  setFigmaRefreshLoading: (value: string | null) => void;
  taskId?: string | null;
  isTaskDetailPreview?: boolean;
  figmaRefreshLoading: string | null;
  setSubTaskOpen: (value: boolean) => void;
  setOpen: (value: boolean) => void;
  onUpdateStatus: (task: TaskModel, status: StatusListInterface, previousStatus: StatusListInterface) => void;
  onClickClone: (task: TaskModel) => void;
  onClickEdit: (taskDetails: TaskModel) => void;
  Opendeletemodel: (taskId: string, subTask: boolean) => void;
  loadData: () => void;
  subTaskOpen: boolean;
  isHoverSelect?: boolean;
  hoveredSubTaskId?: React.MutableRefObject<string>;
  selectedSubTaskIds?: {
    taskId: string;
    groupId: string;
  }[];
}
const TaskInfo: React.FC<Props> = (props) => {
  const {
    onClickTaskCheckbox,
    taskId,
    isTaskDetailPreview,
    onUpdateStatus,
    setOpen,
    setSubTaskOpen,
    figmaRefreshLoading,
    loadData,
    Opendeletemodel,
    onClickEdit,
    onClickClone,
    subTaskOpen,
    isHoverSelect,
    hoveredSubTaskId,
    selectedSubTaskIds
  } = props;

  //States
  const [currentActiveTask, setCurrentActiveTask] = useState<string>('');
  const [deleteModelOpen, setDeleteModelopen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [itemData, setItemData] = useState<TaskFileData | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [subTaskGenerateLoading, setSubTaskGenerateLoading] = useState(false);
  const [fileDeleteModalOpen, setFileDeleteModalOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<{ index: number; file: RawFile } | null>(null);
  const [uploadFileDetail, setUploadFileDetail] = useState<RawFile[]>();
  const [fileUploadLoading, setFileUploadLoading] = useState<boolean>(false);
  const [activeSubTaskId, setActiveSubTaskId] = useState<string | null>(null);

  // Ref variables
  const editorRef = useRef<TiptapEditorRef>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  //use selector state variables
  const { currentUser } = rootStore;

  //Mobx state variables
  const { taskStore, projectStore } = rootStore;
  const { currentTask: taskDetails } = taskStore;
  const { currentProject, uploadFilePercent } = projectStore;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const subTaskList = taskDetails?.subTasks || [];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const parentTask = taskDetails?.parentTask;

  // Other variables
  const mobile = useMobileDevice();
  const history = useHistory();
  // Save description on blur
  const initialDescriptionRef = useRef<string>('');
  const completedTaskPercentage = (subTaskList?.filter((item) => item.status).length / subTaskList?.length) * 100;
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const subTaskIds = useMemo(() => (subTaskList as TaskModel[])?.map((t) => t.id) || [], [subTaskList]);

  const draggingStyle = useMemo(
    () => ({
      userSelect: 'none' as const,
      background: 'var(--background-primary)',
      borderRadius: 40,
      border: '0.5px solid var(--border-primary)',
      boxShadow: 'var(--shadow-card-hover)'
    }),
    []
  );

  const normalStyle = useMemo(
    () => ({
      userSelect: 'none' as const,
      background: 'none',
      borderRadius: 40,
      border: '0.5px solid transparent',
      boxShadow: 'none'
    }),
    []
  );

  const isSubTaskSelected = useCallback(
    (id: string) => {
      return selectedSubTaskIds?.some((item) => item.taskId === id);
    },
    [selectedSubTaskIds]
  );

  // Reusable helper to calculate status details
  const getStatusDetails = useCallback(
    (item: TaskModel): StatusListInterface | undefined => {
      if (currentProject?.statusEnable && currentProject?.statusData?.length > 0) {
        if (!item?.statusId) return currentProject?.defaultStatus;
        return currentProject?.statusData?.find((x) => x['_id'] === item?.statusId) || currentProject?.defaultStatus;
      }
      return undefined;
    },
    [currentProject]
  );

  const renderSubTaskLabelsAndDue = useCallback(
    (item: TaskModel) => {
      let dueResult: { overDue: boolean; dueTime: string; dueParam?: string } | undefined;
      if (item?.dueDate) {
        dueResult = calculateDueTime(item.dueDate);
      }

      return (
        <Right>
          <MultipleLabel>
            {item?.labels?.map((label, index) => {
              if (index < 2) {
                return (
                  <DropBlock className={index === 0 ? 'left-border' : ''} key={label?.id}>
                    <DotIcon color={`${LABEL_COLOUR_HASH[label?.color]}`} />
                    <DropLabel>{label?.name}</DropLabel>
                  </DropBlock>
                );
              } else if (index === 2) {
                const remainingLabels = item?.labels?.length - 3;
                if (remainingLabels > 0) {
                  const tooltipContent = (
                    <MainTooltipDiv>
                      {item?.labels?.slice(3).map((l) => (
                        <TooltipDiv key={l.id}>
                          <DotIcon color={`${LABEL_COLOUR_HASH[l.color]}`} />
                          <span>{l.name}</span>
                        </TooltipDiv>
                      ))}
                    </MainTooltipDiv>
                  );
                  return (
                    <Tooltip title={tooltipContent} maxWidth={'max-content'}>
                      <DropBlock key={label?.id}>
                        <DropLabel>+{remainingLabels} more</DropLabel>
                      </DropBlock>
                    </Tooltip>
                  );
                } else {
                  return (
                    <DropBlock key={label?.id}>
                      <DotIcon color={`${LABEL_COLOUR_HASH[label?.color]}`} />
                      <DropLabel>{label?.name}</DropLabel>
                    </DropBlock>
                  );
                }
              }
              return null;
            })}
          </MultipleLabel>
          {item?.dueDate && dueResult && (
            <DueDate role='button' onClick={(e) => e.stopPropagation()}>
              <Tooltip
                title={
                  <>
                    <div>Due {dueResult?.dueParam}</div>
                    <div>Due on {dayjs(item?.dueDate).format(currentUser?.dateFormat)}</div>
                  </>
                }
                placement='bottom'
                maxWidth={'max-content'}>
                <TooltipContent>
                  <DetailTask isOverdue={dueResult?.overDue && !item?.status}>{dueResult?.dueTime}</DetailTask>
                  {dueResult?.overDue && !item?.status ? (
                    <SVGIcon
                      name='tasklist-calendar-duedate-icon'
                      width={ICON_SIZE.XXL}
                      height={ICON_SIZE.XXL}
                      viewBox={`0 0 ${ICON_SIZE.XXL} ${ICON_SIZE.XXL}`}
                      fill='var(--accent-error)'
                    />
                  ) : (
                    <SVGIcon
                      name='tasklist-calendar-icon'
                      width={ICON_SIZE.XXL}
                      height={ICON_SIZE.XXL}
                      viewBox={`0 0 ${ICON_SIZE.XXL} ${ICON_SIZE.XXL}`}
                      fill='var(--text-secondary)'
                    />
                  )}
                </TooltipContent>
              </Tooltip>
            </DueDate>
          )}
        </Right>
      );
    },
    [currentUser?.dateFormat]
  );

  // open native file picker
  const handleFileInput = useCallback(() => {
    if (fileInputRef.current) fileInputRef.current.click();
  }, []);

  // Helper to normalize description and upload embedded images to Firebase
  const processDescriptionHtml = useCallback(async (htmlString: string): Promise<string> => {
    try {
      if (!htmlString) return '';
      let descriptionData = htmlString || '';

      // remove leading/trailing empty paragraphs like <p><br></p>
      const pattern = /(^<p><br><\/p>\s*)|(\s*<p><br><\/p>$)/;
      if (!isEmpty(descriptionData)) {
        while (pattern.test(descriptionData)) {
          descriptionData = descriptionData.replace(pattern, '');
        }
        if (descriptionData.match(/<br\s*>\s*<\/p>$/))
          descriptionData = descriptionData.replace(/<br\s*>\s*<\/p>$/, '</p>');
      }

      // Parse HTML and upload inline images (base64/blob) to Firebase
      const tempElement = document.createElement('div');
      tempElement.innerHTML = descriptionData || '';
      const imgElements = tempElement.querySelectorAll('img');

      const uploadPromises: Promise<void>[] = [];
      imgElements.forEach((imgEl) => {
        const myPromise = (async () => {
          const srcLink = imgEl?.getAttribute('src');
          if (!srcLink) return;
          // Skip if already hosted or static
          if (
            srcLink.startsWith('https://firebasestorage') ||
            srcLink.startsWith('https://figma') ||
            srcLink.startsWith('/static') ||
            srcLink.startsWith('https://')
          ) {
            return;
          }
          try {
            const fileData = await convertBase64ToFile(srcLink);
            if (!fileData) return;
            const fileUrl = await getFirebaseUrlFromFile(
              { file: fileData },
              buildTaskDescriptionImageFolder(taskDetails?.projectId)
            );
            if (fileUrl) {
              descriptionData = descriptionData.replace(srcLink, fileUrl);
            }
          } catch (error) {
            captureException(error);
          }
        })();
        uploadPromises.push(myPromise);
      });

      await Promise.all(uploadPromises);
      return descriptionData;
    } catch (e) {
      captureException(e);
      return htmlString || '';
    }
  }, [taskDetails?.projectId]);

  const handleDescriptionBlur = useCallback(async () => {
    try {
      if (!taskDetails?.id) return;
      const currentDescription = taskDetails?.description || '';
      if (currentDescription === initialDescriptionRef.current) return;

      const processedHtml = await processDescriptionHtml(currentDescription);
      rootStore.taskStore.currentTask?.setDescription(processedHtml || '');
      const payloadTask = { description: processedHtml, projectId: taskDetails?.projectId };
      const response = await updateTaskDetails(taskDetails?.id, payloadTask);
      if (response) {
        // Add the activity row locally too, the API call alone does not put it in the list.
        if (currentUser) {
          taskDetails.upsertComment(getRawComment({ id: nanoid(), type: COMMENT_TYPE.UPDATE_TASK }, currentUser));
        }
        await createNewComment(taskDetails.id, {
          Type: COMMENT_TYPE.UPDATE_TASK
        });
      }
      initialDescriptionRef.current = processedHtml || '';
    } catch (e) {
      // noop; do not throw from blur
    }
  }, [taskDetails, processDescriptionHtml, currentUser]);

  // Handle upload completion with focus check
  const handleUploadCompleted = useCallback(async () => {
    try {
      const isEditorFocused = editorRef.current?.isFocused?.();
      if (!isEditorFocused) {
        await handleDescriptionBlur();
      }
    } catch (e) {
      // noop;
    }
  }, [handleDescriptionBlur]);

  // upload files selected from picker and append to task files
  const onFileUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const { files } = event.target || {};
      if (!files || files.length === 0) return;
      try {
        setFileUploadLoading(true);
        const fileArray = Array.from(files);
        setUploadFileDetail([{ name: fileArray?.[0]?.name } as RawFile]);

        // Use shared helper to upload and build file references
        const updatedFilesRef = await handleFileUpload(
          (taskDetails?.files as RawFile[]) || [],
          fileArray,
          editorRef,
          undefined,
          buildTaskAttachmentFolder(taskDetails?.projectId)
        );

        if (updatedFilesRef && updatedFilesRef.length > 0 && taskDetails?.id) {
          const updatedFiles = [...(taskDetails?.files || []), ...updatedFilesRef];
          taskDetails.setFiles(updatedFiles as RawFile[]);
          const payload = { projectId: taskDetails?.projectId, files: updatedFiles };
          await updateTaskDetails(taskDetails.id, payload);
        }
      } catch (e) {
        captureException(e);
        rootStore.alertNotificationStore.setErrorMessage('Failed to upload files');
      } finally {
        setUploadFileDetail([]);
        setFileUploadLoading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    },
    [taskDetails]
  );

  //handle click user
  const onClickUsers = useCallback(
    (taskId: string) => {
      if (mobile) {
        rootStore.taskStore.setCurrentAssignBoxTaskId(taskId);
      }
    },
    [mobile]
  );

  useEffect(() => {
    return () => {
      rootStore.taskStore.clearCurrentAssignBoxTaskId();
    };
  }, []);

  const calculateUpdatedTaskIndex = useCallback(
    (destinationIndex: number, sourceIndex: number): number => {
      const draggedId = (subTaskList as TaskModel[])?.[sourceIndex]?.id;
      const workingList: TaskModel[] = ((subTaskList as TaskModel[]) || []).filter((t) => t.id !== draggedId);

      let updatedTaskIndex: number;

      if (destinationIndex === 0) {
        const topIdx = workingList?.[0]?.subtaskIndex;
        updatedTaskIndex = typeof topIdx === 'number' ? topIdx + 500 : new Date().getTime();
      } else if (destinationIndex >= workingList.length) {
        const bottomIdx = workingList?.[workingList.length - 1]?.subtaskIndex;
        updatedTaskIndex = typeof bottomIdx === 'number' ? bottomIdx - 500 : new Date().getTime() - 500;
      } else {
        const before = workingList?.[destinationIndex - 1]?.subtaskIndex;
        const after = workingList?.[destinationIndex]?.subtaskIndex;
        const beforeIdx = typeof before === 'number' ? before : 0;
        const afterIdx = typeof after === 'number' ? after : 0;
        updatedTaskIndex = (beforeIdx + afterIdx) / 2;
      }
      return updatedTaskIndex;
    },
    [subTaskList]
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveSubTaskId(event.active.id as string);
    // Inject cursor override styles
    const style = document.createElement('style');
    style.id = 'drag-cursor-override';
    style.textContent = `
      * { cursor: grabbing !important; }
      [data-dnd-kit-drag-handle] { cursor: grabbing !important; }
      [data-dnd-kit-draggable] { cursor: grabbing !important; }
    `;
    document.head.appendChild(style);
  }, []);

  //subtask drag and drop
  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      setActiveSubTaskId(null);
      // Remove cursor override styles
      const styleElement = document.getElementById('drag-cursor-override');
      if (styleElement) {
        document.head.removeChild(styleElement);
      }
      try {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const draggableId = String(active.id);
        const subTasks = subTaskList as TaskModel[];
        const sourceIndex = subTasks.findIndex((t) => t.id === active.id);
        const destinationIndex = subTasks.findIndex((t) => t.id === over.id);
        if (sourceIndex === -1 || destinationIndex === -1) return;

        const updatedTaskIndex = calculateUpdatedTaskIndex(destinationIndex, sourceIndex);
        const draggedTask = currentProject?.tasks?.find((task) => task.id === draggableId);
        if (!draggedTask) return;
        draggedTask.setSubtaskIndex(updatedTaskIndex);
        const payload = {
          projectId: taskDetails?.projectId,
          subtaskIndex: updatedTaskIndex
        };
        updateTaskDetails(draggableId, payload);
      } catch (error) {
        captureException(error);
      }
    },
    [subTaskList, currentProject?.tasks, taskDetails?.projectId, calculateUpdatedTaskIndex]
  );

  const handleDescriptionFocus = useCallback(() => {
    initialDescriptionRef.current = taskDetails?.description || '';
  }, [taskDetails?.description]);

  // Opening another task from a task card keeps this editor mounted and focused, so the next blur
  // would compare the new task against the old task's baseline and save it. Reset the baseline and drop focus.
  useEffect(() => {
    initialDescriptionRef.current = rootStore.taskStore.currentTask?.description || '';
    // Tiptap's blur also clears the document selection, so only call it when this editor holds focus.
    if (editorRef.current?.isFocused?.()) editorRef.current.messageInputBlur();
  }, [taskDetails?.id]);

  // handle File card click in task description
  const handleTaskAreaFileCard = useCallback(
    (e: React.SyntheticEvent, x: CommentFilesInterface) => {
      if (
        x?.uploadFileType &&
        [FILE_TYPES.DRIVE, FILE_TYPES.DROPBOX, FILE_TYPES.NOTION, FILE_TYPES.FIGMA, FILE_TYPES.ONEDRIVE].includes(
          x?.uploadFileType
        )
      ) {
        window.open(x?.href, '_blank', 'noopener,noreferrer');
        return;
      }
      const fileName = x?.name?.replaceAll(' ', '_');
      e.preventDefault();
      const path = x?.href?.startsWith('/projects/files')
        ? x?.href
        : `/projects/details/${taskDetails?.projectId}/files/file-view/${encodeURIComponent(
            fileName
          )}?fileUrl=${encodeURIComponent(x?.href)}`;
      history.push(path);
    },
    [history, taskDetails?.projectId]
  );

  const menuItems = [
    {
      key: MENU_OPTIONS.COPYLINK,
      label: 'Copy link'
    },
    {
      key: MENU_OPTIONS.EDIT,
      label: 'Edit link'
    },
    {
      key: MENU_OPTIONS.DELETE,
      label: 'Delete',
      tone: 'critical'
    }
  ];

  const handleClick = useCallback(async (key: number | string, file: TaskFileData) => {
    key = Number(key);
    if (key === MENU_OPTIONS.COPYLINK) {
      await copy(file?.url);
      rootStore.alertNotificationStore.setSuccessMessage(`Link copied successfully!`);
    } else if (key === MENU_OPTIONS.EDIT) {
      setItemData(file);
      setIsOpen(true);
    } else if (key === MENU_OPTIONS.DELETE) {
      setItemData(file);
      setDeleteModelopen(true);
    }
  }, []);

  const close = () => {
    setDeleteModelopen(false);
    setItemData(null);
  };

  // for open model
  const openModel = useCallback(() => {
    setIsOpen(true);
  }, []);

  // for cancel model
  const cancelModel = useCallback(() => {
    setIsOpen(false);
    setItemData(null);
  }, []);

  // For delete link
  const deleteLinkData = useCallback(async () => {
    try {
      setDeleteLoading(true);
      const response = await deleteTaskLink(taskId, itemData?.id);
      if (response && itemData?.id) {
        rootStore.taskStore.currentTask?.removeLink(itemData?.id);
      }
    } catch (error) {
      captureException(error);
    } finally {
      setDeleteLoading(false);
      close();
    }
  }, [itemData?.id, taskId]);

  //create sub task
  const onClickSubTask = useCallback(async () => {
    if (!taskDetails || !taskDetails?.project?.id) return;
    rootStore.taskStore.createTaskInput.clear();
    rootStore.taskStore.createTaskInput.setProject(taskDetails?.project?.id);
    rootStore.taskStore.createTaskInput.setParentTask(taskDetails?.id);
    setOpen(true);
  }, [setOpen, taskDetails]);

  //handle click sub task
  const onClickTaskDetails = useCallback(
    (task: TaskModel) => {
      rootStore.taskStore.setCurrentTask(task?.id);
      history.push(ROUTES.TASK_DETAILS.replace(':id', task?.projectId || '').replace(':taskId', task?.id || ''));
    },
    [history]
  );

  const handleIconClick = async () => {
    if (!taskDetails?.id) return; //make sure parent task exists
    try {
      setSubTaskGenerateLoading(true);

      const payload = {
        taskId: taskDetails.id,
        projectId: taskDetails?.project?.id,
        description: taskDetails?.description,
        name: taskDetails?.name
      };

      // Call service instead of mock response
      const response = await generateSubTasks(payload);

      if (response?.subTasks?.length) {
        // prepare default payload (base)
        const basePayload = {
          // Prefer parent task’s project, fallback to createTaskInput
          projectId: taskDetails?.project?.id || rootStore.taskStore.createTaskInput?.project?.id || null,

          groupId: taskDetails?.project?.isGroupEnabled ? rootStore.taskStore.createTaskInput?.groupId : null,

          dueDate: rootStore.taskStore.createTaskInput?.dueDate,
          status: false,
          files: !isEmpty(rootStore.taskStore.createTaskInput?.files) ? rootStore.taskStore.createTaskInput?.files : [],

          parentTaskId: taskDetails.id, // Always link to parent

          statusId: taskDetails?.project?.statusEnable
            ? rootStore.taskStore.createTaskInput?.statusId || taskDetails?.project?.defaultStatus?.['_id']
            : undefined
        };

        // map AI response to subtask payloads
        const subTaskPayloads = response.subTasks.map((st: GeneratedSubTask) => {
          const subPayload: SubTaskPayload = {
            ...basePayload,
            name: st.name,
            description: st.description
          };

          //Conditionally add fields
          if (taskDetails?.project?.isEstimate) {
            subPayload.estimateTime =
              typeof st.estimate === 'number' ? st.estimate : getFloatTimeValue(st.estimate || '');
          }

          if (taskDetails?.project?.priority?.enabled) {
            subPayload.priority = st.priority ?? rootStore.taskStore.createTaskInput?.priority;
          }
          return subPayload;
        });
        const result = await createBulkTask(subTaskPayloads);
        if (result) {
          dispatchTaskSuccess('Subtasks created successfully');
        }
      } else {
        dispatchTaskError('No subtasks returned by AI');
      }
    } catch (error) {
      captureException(error);
    } finally {
      setSubTaskGenerateLoading(false);
    }
  };

  const addDroppedFiles = useCallback(
    async (files: RawFile[]) => {
      try {
        setFileUploadLoading(true);
        // If there's a current task, update it with the new files
        if (taskDetails?.id) {
          setUploadFileDetail(files);
          // Create the updated files array
          const updatedFiles = [...(taskDetails.files || []), ...files];
          taskDetails.setFiles(updatedFiles);

          // Prepare payload for API call
          const payload = {
            projectId: taskDetails?.projectId,
            files: updatedFiles
          };
          // Update in backend via service
          await updateTaskDetails(taskDetails.id, payload);
        }
      } catch (error) {
        captureException(error);
        // Revert the changes in MobX if API call fails
        if (taskDetails?.id) {
          taskDetails.setFiles(taskDetails.files || []);
        }
        rootStore.alertNotificationStore.setErrorMessage('Failed to upload files');
      } finally {
        setUploadFileDetail([]);
        setFileUploadLoading(false);
      }
    },
    [taskDetails]
  );

  const handleFileClose = useCallback((fileIndex: number, file: RawFile) => {
    setFileToDelete({ index: fileIndex, file });
    setFileDeleteModalOpen(true);
  }, []);

  // Actually delete the file after confirmation
  const confirmFileDelete = useCallback(async () => {
    if (!fileToDelete || !taskDetails?.id) return;
    try {
      const { index, file } = fileToDelete;
      // Create updated files array without the deleted file
      const updatedFiles = taskDetails.files?.filter((_, fileIndex) => fileIndex !== index) || [];
      taskDetails.setFiles(updatedFiles);
      // Prepare payload for API call
      const payload = {
        projectId: taskDetails?.projectId,
        files: updatedFiles
      };
      // Update in backend via service
      await updateTaskDetails(taskDetails.id, payload);
      // Remove the uploaded file from Firebase storage only after the backend no longer references it;
      // skip selected/shared project files
      if (file && !file.uploadFileType && file.href?.startsWith('https://firebasestorage.googleapis')) {
        removeFileFromFirebase(file.href);
      }
      rootStore.alertNotificationStore.setSuccessMessage('File deleted successfully');
    } catch (error) {
      captureException(error);
      // Revert the changes in MobX if API call fails
      if (taskDetails?.id) {
        taskDetails.setFiles(taskDetails.files || []);
      }
      rootStore.alertNotificationStore.setErrorMessage('Failed to delete file');
    } finally {
      setFileDeleteModalOpen(false);
      setFileToDelete(null);
    }
  }, [fileToDelete, taskDetails]);

  // Close file delete modal
  const closeFileDeleteModal = useCallback(() => {
    setFileDeleteModalOpen(false);
    setFileToDelete(null);
  }, []);

  // render pull request data
  const renderPullRequestData = useMemo(() => {
    if (!taskDetails?.pullRequests) {
      return null;
    }
    return (
      <>
        {taskDetails?.pullRequests?.map((pullData) => (
          <GithubMainDiv role='button' onClick={() => window.open(pullData?.url, '_blank')} key={pullData.number}>
            <GithubSmallIcon>
              <SVGIcon
                name='github-small-icon'
                width={ICON_SIZE.SM}
                height={ICON_SIZE.SM}
                viewBox={`0 0 ${ICON_SIZE.SM} ${ICON_SIZE.SM}`}
                className='github-icon'
              />
            </GithubSmallIcon>
            <BottomContent>
              <MergedLabel>
                <Texts>{pullData.number}</Texts>
                <GithubTitle>{pullData.title}</GithubTitle>
                <Dot />
              </MergedLabel>
              <LabelContainer>
                {pullData.state === 'open' ? (
                  <LabelIcon>
                    <SVGIcon
                      name='branch-opened-icon'
                      width={ICON_SIZE.SM}
                      height={ICON_SIZE.SM}
                      viewBox={`0 0 ${ICON_SIZE.SM} ${ICON_SIZE.SM}`}
                    />
                  </LabelIcon>
                ) : pullData.merged ? (
                  <LabelIcon>
                    <SVGIcon
                      name='branch-merged-icon'
                      width={ICON_SIZE.SM}
                      height={ICON_SIZE.SM}
                      viewBox={`0 0 ${ICON_SIZE.SM} ${ICON_SIZE.SM}`}
                    />
                  </LabelIcon>
                ) : (
                  <LabelIcon>
                    <SVGIcon
                      name='branch-closed-icon'
                      width={ICON_SIZE.SM}
                      height={ICON_SIZE.SM}
                      viewBox={`0 0 ${ICON_SIZE.SM} ${ICON_SIZE.SM}`}
                    />
                  </LabelIcon>
                )}
                <MergeText>{pullData.state === 'open' ? 'Opened' : pullData.merged ? 'Merged' : 'Closed'}</MergeText>
              </LabelContainer>
            </BottomContent>
          </GithubMainDiv>
        ))}
      </>
    );
  }, [taskDetails?.pullRequests]);

  // // Collaboration configuration
  // const collaborationUser = useMemo(
  //   () => ({
  //     id: currentUser?.id || 'anonymous',
  //     name: currentUser?.name || 'Anonymous',
  //     color: getRandomCollaborationColor(),
  //     profilePhoto: currentUser?.profile_photo
  //   }),
  //   [currentUser?.id, currentUser?.name, currentUser?.profile_photo]
  // );

  // // Generate unique document name for task description collaboration
  // const collaborationDocumentName = useMemo(() => {
  //   if (!taskDetails?.id || !taskDetails?.projectId) return '';
  //   return `task-description-${taskDetails.projectId}-${taskDetails.id}`;
  // }, [taskDetails?.id, taskDetails?.projectId]);

  // created a function to handle link open in new tab or the same tab
  const handleLinkClick = useCallback(
    (reference?: string) => {
      if (!reference) return; // If no link, do nothing

      try {
        // created url object to support relative paths
        const url = new URL(reference, window.location.origin);
        // check if hostName matches the domain
        const isSameDomain = url.hostname === window.location.hostname;

        if (isSameDomain) {
          // If it's the same domain, use SPA navigation to avoid full page reload
          history.push(url.pathname + url.search + url.hash);
        } else {
          //  if domain not matches open in new tab
          window.open(reference, '_blank', 'noopener,noreferrer');
        }
      } catch (e) {
        // Catch errors for invalid URLs (e.g. malformed strings)
        captureException(e);
      }
    },
    [history]
  );

  return (
    <>
      <HeaderContent isTaskDetailPreview={isTaskDetailPreview}>
        <SubTaskList className={taskDetails?.parentTaskId && parentTask ? 'has-maintask' : ''}>
          <Linkify
            componentDecorator={(decoratedHref: string, decoratedText: string, key: number) => (
              <a target='blank' href={decoratedHref} key={key}>
                {decoratedText}
              </a>
            )}>
            <TextBlock>
              <SubtaskTitle isMobile={mobile && !parentTask}>
                {!mobile && (
                  <FormBlock>
                    <Form>
                      {taskDetails?.project?.statusEnable ? (
                        <TaskStatusDropdownV2
                          defaultStatus={currentProject?.defaultStatus}
                          statusList={currentProject?.statusData || []}
                          currentStatusId={taskDetails?.statusId}
                          onUpdateStatus={(status: StatusListInterface, previousStatus: StatusListInterface) =>
                            onUpdateStatus(taskDetails!, status, previousStatus)
                          }
                          isTaskCheckBox={true}
                          isIconButton={true}
                        />
                      ) : (
                        <div
                          role='button'
                          className='img'
                          onClick={() => onClickTaskCheckbox(taskDetails!, !taskDetails?.status)}>
                          <SVGIcon
                            name={taskDetails?.status ? 'complete-icon' : 'open-icon'}
                            width={ICON_SIZE.XL}
                            height={ICON_SIZE.XL}
                            viewBox={`0 0 ${ICON_SIZE.MD} ${ICON_SIZE.MD}`}
                            className={taskDetails?.status ? 'complete-icon' : 'task-open-icon'}
                          />
                        </div>
                      )}
                      {/* task title */}
                      <TaskTitle />
                    </Form>
                  </FormBlock>
                )}
                {!isEmpty(taskDetails?.parentTaskId) && parentTask && (
                  <MainTask>
                    <span>Sub task of</span>
                    <Form2 role='button' onClick={() => onClickTaskDetails(parentTask)}>
                      {currentProject?.statusEnable ? (
                        <TaskStatusDropdownV2
                          defaultStatus={currentProject?.defaultStatus}
                          statusList={currentProject?.statusData || []}
                          currentStatusId={parentTask?.statusId}
                          isTaskCheckBox={true}
                          onUpdateStatus={(status: StatusListInterface, previousStatus: StatusListInterface) =>
                            onUpdateStatus(parentTask, status, previousStatus)
                          }
                          isIconButton={true}
                        />
                      ) : (
                        <CheckboxInput
                          checked={parentTask?.status}
                          onClick={(e) => {
                            e.stopPropagation();
                            parentTask && onClickTaskCheckbox(parentTask, !parentTask.status);
                          }}
                          checkboxType='task-checkbox'
                        />
                      )}
                      <Linkify
                        componentDecorator={(decoratedHref: string, decoratedText: string, key: number) => (
                          <a target='blank' href={decoratedHref} key={key}>
                            {decoratedText}
                          </a>
                        )}>
                        <TaskLabel>{parentTask?.name}</TaskLabel>
                      </Linkify>
                    </Form2>
                    {parentTask?.subTasks && (
                      <TaskCount>
                        {parentTask?.subTasks?.filter((x) => x?.status)?.length}/{parentTask?.subTasks?.length}
                      </TaskCount>
                    )}
                  </MainTask>
                )}
              </SubtaskTitle>

              {/* task Description */}
              {taskDetails?.id && (
                <TaskArea
                  figmaRefreshLoading={figmaRefreshLoading === taskDetails?.id}
                  onFocus={handleDescriptionFocus}
                  onBlur={handleDescriptionBlur}>
                  <TiptapEditor
                    ref={editorRef}
                    valueContent={taskDetails?.description || ''}
                    isEditable={true}
                    placeholder='Add Description....'
                    setValueContent={(value: string) => rootStore.taskStore.currentTask?.setDescription(value)}
                    upload={{
                      addUploadedFiles: addDroppedFiles,
                      onUploadCompleted: handleUploadCompleted,
                      uploadFolder: buildTaskDescriptionImageFolder(taskDetails?.projectId)
                    }}
                    dropBox={{
                      isVisible: true,
                      topSpacing: 10,
                      width: '-webkit-fill-available',
                      isTaskDescription: true
                    }}
                    // Collaboration props
                    // documentName={collaborationDocumentName}
                    // collaborationUser={collaborationUser}
                  />
                  {taskDetails?.files && taskDetails?.files?.length > 0 && (
                    <TaskAreaFile>
                      {taskDetails?.files?.map((x: RawFile) => {
                        return (
                          <>
                            <RenderFilePreview
                              key={x?.href}
                              file={x as CommentFilesInterface}
                              maxWidth={240}
                              onClickUpdate={(e) => handleTaskAreaFileCard(e, x as CommentFilesInterface)}
                              isClose={true}
                              onClose={() => handleFileClose(taskDetails?.files?.indexOf(x), x)}
                            />
                          </>
                        );
                      })}
                    </TaskAreaFile>
                  )}
                </TaskArea>
              )}

              {/* Attachment icon and hidden input to add files to description */}
              <AttachmentContainer>
                <IconButton
                  iconName='file-attachment-icon'
                  size={ICON_BUTTON_SIZE.MEDIUM}
                  variant={ICON_BUTTON_TYPE.TERTIARY}
                  onClick={handleFileInput}
                />
                <HiddenFileInput ref={fileInputRef} type='file' multiple onChange={onFileUpload} />
              </AttachmentContainer>
            </TextBlock>
          </Linkify>
          {/* GITHUB REPOSITORY UI */}
          {renderPullRequestData && (taskDetails?.pullRequests?.length || 0) > 0 && (
            <GithubPRSection>{renderPullRequestData}</GithubPRSection>
          )}
          <SubTaskSection>
            <AddSubTaskSection>
              {subTaskList?.length === 0 ? (
                <>
                  {isEmpty(taskDetails?.parentTaskId) && (
                    <SubTaskDiv>
                      <AddSubTask onClick={onClickSubTask}>
                        <SVGIcon
                          name='plus-icon'
                          width={ICON_SIZE.SM}
                          height={ICON_SIZE.SM}
                          viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                          fill='var(--text-secondary)'
                        />
                        <p>Add Subtask</p>
                      </AddSubTask>
                      <Tooltip title='Generate Subtask from AI' placement='bottom' maxWidth={'max-content'}>
                        <IconDiv isLoading={subTaskGenerateLoading} role='button' onClick={handleIconClick}>
                          {subTaskGenerateLoading ? (
                            <Image src={AiLoader} alt='ai loader' width={16} height={16} />
                          ) : (
                            <SVGIcon
                              name='ai-icon'
                              width={ICON_SIZE.MD}
                              height={ICON_SIZE.MD}
                              viewBox={`0 0 ${ICON_SIZE.MD} ${ICON_SIZE.MD}`}
                            />
                          )}
                        </IconDiv>
                      </Tooltip>
                    </SubTaskDiv>
                  )}
                </>
              ) : (
                <>
                  <SubtaskHeadLeft>
                    <p>Sub tasks</p>
                    <TaskIndicator completePercentage={completedTaskPercentage}>
                      <div className='circle'>
                        <div className='innerCircle' />
                      </div>
                      <TaskIndicatorText>{`${subTaskList?.filter((task) => task.status).length}/${
                        subTaskList?.length
                      }`}</TaskIndicatorText>
                    </TaskIndicator>
                  </SubtaskHeadLeft>
                </>
              )}
              {!taskDetails?.parentTaskId && (
                <SubtaskHeadRight>
                  {subTaskList?.length !== 0 && (
                    <IconButton
                      iconName='plus-icon'
                      size={ICON_BUTTON_SIZE.MEDIUM}
                      variant={ICON_BUTTON_TYPE.TERTIARY}
                      onClick={onClickSubTask}
                    />
                  )}
                  <DropDiv>
                    <Dropdown
                      modalTitle='Link Task'
                      activeClassName='active-state'
                      content={
                        <IconButton
                          iconName='three-dots-horizontal-icon'
                          size={ICON_BUTTON_SIZE.MEDIUM}
                          variant={ICON_BUTTON_TYPE.TERTIARY}
                        />
                      }
                      trigger='click'>
                      <DropdownItem
                        label='Link existing task as sub-Task…'
                        onClick={() => setSubTaskOpen(!subTaskOpen)}
                        iconName='create-sub-task-icon'
                        iconSize={16}
                        iconViewBox='0 0 18 18'
                      />
                    </Dropdown>
                  </DropDiv>
                </SubtaskHeadRight>
              )}
            </AddSubTaskSection>

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragCancel={() => {
                setActiveSubTaskId(null);
                // Remove cursor override styles
                const styleElement = document.getElementById('drag-cursor-override');
                if (styleElement) {
                  document.head.removeChild(styleElement);
                }
              }}
              onDragEnd={handleDragEnd}>
              <SortableContext items={subTaskIds} strategy={verticalListSortingStrategy}>
                {subTaskList?.length > 0 && (
                  <DivSubTask>
                    {subTaskList?.map((item) => {
                      return (
                        <SortableSubTaskItem key={item?.id} id={item?.id}>
                          {({ isDragging, dragHandleAttributes, dragHandleListeners }) => (
                            <MyCustomContextMenuV2
                              taskData={item}
                              loadData={loadData}
                              currentProject={currentProject!}
                              setCurrentActiveTask={(taskId: string) => setCurrentActiveTask(taskId)}
                              onClickView={() => onClickTaskDetails(item)}
                              onClickClone={() => onClickClone(item)}
                              onClickEdit={() => onClickEdit(item)}
                              onClickDelete={() => Opendeletemodel(item.id, true)}
                              statusList={currentProject?.statusData || []}
                              onUpdateStatus={onUpdateStatus}
                              isSubTask={true}>
                              <Subtask
                                onClick={() => onClickTaskDetails(item)}
                                isActive={item?.id === currentActiveTask}
                                isSelected={isSubTaskSelected(item?.id)}
                                className={isSubTaskSelected(item?.id) ? 'selected-task' : ''}
                                onMouseEnter={() => {
                                  if (isHoverSelect && hoveredSubTaskId) {
                                    hoveredSubTaskId.current = item?.id;
                                  }
                                }}
                                onMouseLeave={() => {
                                  if (isHoverSelect && hoveredSubTaskId) {
                                    hoveredSubTaskId.current = '';
                                  }
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    onClickTaskDetails(item);
                                  }
                                }}
                                style={isDragging ? draggingStyle : normalStyle}>
                                <TaskItem>
                                  <IconHidden
                                    isDragging={isDragging}
                                    onClick={(e) => e.stopPropagation()}
                                    // eslint-disable-next-line react/jsx-props-no-spreading
                                    {...dragHandleAttributes}
                                    // eslint-disable-next-line react/jsx-props-no-spreading
                                    {...dragHandleListeners}>
                                    <SVGIcon
                                      name='new-three-dots-vertical-icon'
                                      width={ICON_SIZE.MD}
                                      height={ICON_SIZE.MD}
                                      viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                                      fill='var(--text-secondary)'
                                    />
                                  </IconHidden>
                                  <Form1>
                                    <StatusDropdown>
                                      {getStatusDetails(item) ? (
                                        <TaskStatusDropdownV2
                                          defaultStatus={currentProject?.defaultStatus}
                                          statusList={currentProject?.statusData || []}
                                          currentStatusId={item?.statusId}
                                          isTaskCheckBox={true}
                                          onUpdateStatus={(
                                            status: StatusListInterface,
                                            previousStatus: StatusListInterface
                                          ) => onUpdateStatus(item, status, previousStatus)}
                                          isIconButton={true}
                                        />
                                      ) : (
                                        <ButtonIcon>
                                          <CheckboxInput
                                            checked={item?.status}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              onClickTaskCheckbox(item, !item.status);
                                            }}
                                            checkboxType='task-checkbox'
                                          />
                                        </ButtonIcon>
                                      )}
                                      {currentProject?.priority?.enabled && (
                                        <div role='button' onClick={(e) => e.stopPropagation()}>
                                          <PriorityDropdownV2
                                            taskData={item}
                                            currentProject={currentProject}
                                            isTaskCheckBox={true}
                                            isIconButton={true}
                                          />
                                        </div>
                                      )}
                                    </StatusDropdown>
                                    <Linkify
                                      componentDecorator={(
                                        decoratedHref: string,
                                        decoratedText: string,
                                        key: number
                                      ) => (
                                        <a target='blank' href={decoratedHref} key={key}>
                                          {decoratedText}
                                        </a>
                                      )}>
                                      <TaskTitleDiv>
                                        <Label>{item?.name}</Label>
                                      </TaskTitleDiv>
                                    </Linkify>
                                  </Form1>
                                  <AssignGroup role='button' onClick={(e) => e.stopPropagation()} className='assign'>
                                    <ul role='button' onClick={() => onClickUsers(item?.id)}>
                                      {item?.users
                                        ?.filter((item) => !isEmpty(item?.id))
                                        ?.map((user) => {
                                          return (
                                            <li key={user?.id}>
                                              <AvatarImage>
                                                <Avatar
                                                  imgSrc={user?.profile_photo || ''}
                                                  name={user?.name ? user.name : 'U N'}
                                                  size={20}
                                                  borderVariant={AVATAR_BORDER.THIN}
                                                />
                                              </AvatarImage>
                                            </li>
                                          );
                                        })}
                                      <div role='button' onClick={(e) => e.stopPropagation()}>
                                        <SubTaskAssignBox
                                          task={item}
                                          projectUsers={currentProject?.users || []}
                                          isMultiAssignee={!!currentProject?.multiAssignee}
                                        />
                                      </div>
                                    </ul>
                                  </AssignGroup>
                                </TaskItem>
                                {renderSubTaskLabelsAndDue(item as TaskModel)}
                              </Subtask>
                            </MyCustomContextMenuV2>
                          )}
                        </SortableSubTaskItem>
                      );
                    })}
                  </DivSubTask>
                )}
              </SortableContext>
              <DragOverlay>
                {activeSubTaskId &&
                  (() => {
                    const activeItem = (subTaskList as TaskModel[])?.find((t) => t.id === activeSubTaskId);
                    if (!activeItem) return null;
                    const activeStatusDetails = getStatusDetails(activeItem);
                    return (
                      <Subtask style={{ ...draggingStyle, pointerEvents: 'none' }}>
                        <TaskItem>
                          <IconHidden isDragging={true}>
                            <SVGIcon
                              name='new-three-dots-vertical-icon'
                              width={ICON_SIZE.MD}
                              height={ICON_SIZE.MD}
                              viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                              fill='var(--text-secondary)'
                            />
                          </IconHidden>
                          <Form1>
                            <StatusDropdown>
                              {activeStatusDetails ? (
                                <TaskStatusDropdownV2
                                  defaultStatus={currentProject?.defaultStatus}
                                  statusList={currentProject?.statusData || []}
                                  currentStatusId={activeItem?.statusId}
                                  isTaskCheckBox={true}
                                  onUpdateStatus={(status: StatusListInterface, previousStatus: StatusListInterface) =>
                                    onUpdateStatus(activeItem, status, previousStatus)
                                  }
                                  isIconButton={true}
                                />
                              ) : (
                                <ButtonIcon>
                                  <CheckboxInput
                                    checked={activeItem?.status}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      onClickTaskCheckbox(activeItem, !activeItem.status);
                                    }}
                                    checkboxType='task-checkbox'
                                  />
                                </ButtonIcon>
                              )}
                              {currentProject?.priority?.enabled && (
                                <div role='button' onClick={(e) => e.stopPropagation()}>
                                  <PriorityDropdownV2
                                    taskData={activeItem}
                                    currentProject={currentProject}
                                    isTaskCheckBox={true}
                                    isIconButton={true}
                                  />
                                </div>
                              )}
                            </StatusDropdown>
                            <Label>{activeItem?.name}</Label>
                          </Form1>
                          <AssignGroup className='assign'>
                            <ul>
                              {activeItem?.users
                                ?.filter((u) => !isEmpty(u?.id))
                                ?.map((user) => (
                                  <li key={user?.id}>
                                    <AvatarImage>
                                      <Avatar
                                        imgSrc={user?.profile_photo || ''}
                                        name={user?.name ? user.name : 'U N'}
                                        size={20}
                                        borderVariant={AVATAR_BORDER.THIN}
                                      />
                                    </AvatarImage>
                                  </li>
                                ))}
                            </ul>
                          </AssignGroup>
                        </TaskItem>
                        {renderSubTaskLabelsAndDue(activeItem)}
                      </Subtask>
                    );
                  })()}
              </DragOverlay>
            </DndContext>
            {taskDetails?.links && taskDetails?.links?.length > 0 && (
              <LinkSection>
                <LinkTitle>
                  <p>Links</p>
                  <IconButton
                    iconName='plus-icon'
                    size={ICON_BUTTON_SIZE.MEDIUM}
                    variant={ICON_BUTTON_TYPE.TERTIARY}
                    onClick={openModel}
                  />
                </LinkTitle>
                {taskDetails?.links?.map((link) => {
                  const fileData: TaskFileData = {
                    id: link?.id,
                    name: link?.label || '',
                    url: link?.reference || ''
                  };
                  return (
                    <LinkCard key={link?.id}>
                      <Content>
                        <SocialIcon>
                          {!isEmpty(link?.iconUrl) ? (
                            <Image src={link?.iconUrl} alt={link?.label} width='20' height='20' />
                          ) : (
                            <SVGIcon
                              name={'copy-link-icon'}
                              width={ICON_SIZE.XL}
                              height={ICON_SIZE.XL}
                              viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                              fill='var(--text-primary)'
                            />
                          )}
                        </SocialIcon>
                        <ContentRightSection>
                          <LinkText>
                            <>{link?.label}</>
                          </LinkText>
                          <TimeText>{dayjs(link?.createdTime).fromNow()}</TimeText>
                        </ContentRightSection>
                      </Content>
                      <LinkRightContent>
                        <IconButton
                          iconName='updated-external-link-icon'
                          size={ICON_BUTTON_SIZE.EXTRA_SMALL}
                          variant={ICON_BUTTON_TYPE.TERTIARY}
                          onClick={() => handleLinkClick(link?.reference)}
                        />
                        <DropDiv>
                          <Dropdown
                            modalTitle='Manage Link'
                            activeClassName='active-state'
                            content={
                              <IconButton
                                iconName='three-dots-horizontal-icon'
                                size={ICON_BUTTON_SIZE.EXTRA_SMALL}
                                variant={ICON_BUTTON_TYPE.TERTIARY}
                              />
                            }
                            trigger='click'>
                            {link?.readonly ? (
                              <DropdownItem
                                label={'Copy link'}
                                key={MENU_OPTIONS.COPYLINK}
                                tone={'primary'}
                                onClick={() => handleClick(MENU_OPTIONS.COPYLINK, fileData)}
                              />
                            ) : (
                              menuItems.map((item) => (
                                <DropdownItem
                                  label={item.label}
                                  key={item?.key}
                                  tone={item.label === 'Delete' ? 'critical' : 'primary'}
                                  onClick={() => handleClick(item.key, fileData)}
                                />
                              ))
                            )}
                          </Dropdown>
                        </DropDiv>
                      </LinkRightContent>
                    </LinkCard>
                  );
                })}
              </LinkSection>
            )}
          </SubTaskSection>
        </SubTaskList>
      </HeaderContent>

      <Modal open={deleteModelOpen} onClose={close} maxWidth={'334px'}>
        <DeleteModal
          onClose={close}
          onOk={deleteLinkData}
          loading={deleteLoading}
          modalTitle={'Delete Link?'}
          description={'Are you sure you want to delete this Link?'}
        />
      </Modal>
      <Modal open={isOpen} onClose={cancelModel} maxWidth={'462px'}>
        <LinkFileModal
          onClose={cancelModel}
          modalData={LINK_FILE_DATA.ADDLINK}
          taskId={taskDetails?.id}
          itemData={itemData}
        />
      </Modal>
      <Modal open={fileDeleteModalOpen} onClose={closeFileDeleteModal} maxWidth={'334px'}>
        <DeleteModal
          onClose={closeFileDeleteModal}
          onOk={confirmFileDelete}
          loading={deleteLoading}
          modalTitle={'Delete File?'}
          description={'Are you sure you want to delete this file?'}
        />
      </Modal>
      {(uploadFilePercent || fileUploadLoading) && (
        <FileUploadCard
          fileName={uploadFileDetail?.[0]?.name || 'File Uploading...'}
          extension={extensionName(uploadFileDetail?.[0]).toLowerCase()}
          progress={uploadFilePercent}
          fileSize={0}
        />
      )}
    </>
  );
};

export default observer(TaskInfo);
